import {useEffect, useState} from 'react';
import Avatar from '@jobseeker/lotus/components/Avatar';
import {Box} from '@jobseeker/lotus/foundations';
import {Image} from '@jobseeker/lotus/components/Image';

import Drawer from '@mui/material/Drawer';

import Typography from '@mui/material/Typography';

import Accordion from '@mui/material/Accordion';
import MUIAccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import {Link, List, ListItem, styled} from '@mui/material';
import {
  ChevronDownLight,
  Download,
  Edit,
  Logout,
  SwitchOff,
} from '@jobseeker/lotus/icons';

import type {ProfileDrawerOption, ProfileDrawerProps} from './types';
import Pro from '@jobseeker/lotus/elements/Pro';
import {
  getFilteredCategoryList,
  getOldLink,
  getProLink,
  truncateString,
} from '@jobseeker/lotus/utils';
import {
  openUrl,
  getAppDownloadLinkagesUrls,
  getKeyFromPath,
  deferTask,
} from '@jobseeker/common-core/utils/common';
import ProgressBar from '@jobseeker/lotus/elements/ProgressBar';
import {ButtonV2} from '@jobseeker/lotus/elements';
import {useIsCustomWidth} from '@jobseeker/lotus/hooks';
import Button from '@jobseeker/lotus/elements/Button';
import type {TabProp} from '@jobseeker/lotus/components/Nav/types';
import ProfileCard from '@jobseeker/lotus/components/ProfileCard';
import ProfileDrop from '@jobseeker/lotus/components/ProfileDrop';
import {TrackEventUBA} from '@jobseeker/common-core/utils';

const AccordionSummary = styled(MUIAccordionSummary)(props => ({
  alignItems: 'baseline',
}));

const MenuDrawer = ({
  theme,
  handleDrawer,
  openDrawer,
  tabsList,
  showSpotLight,
  profilePicUrl,
  profileProgress,
  name,
  email,
  phoneVerified,
  proMembership,
  page,
  profileDrawerOptions,
  followUps,
  aboutSection,
  OnLogoutBtnClick,
  proMeta,
  reCap,
  savedJobCount,
  sendEvent,
  loginHandler,
  subscriptions,
  appliedJobCount,
  interviewSlots,
}: ProfileDrawerProps) => {
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [expandProfile, setExpandProfile] = useState(false);
  const isXXS = useIsCustomWidth(350);
  const isMedium = useIsCustomWidth(1024);
  const aboutSectionFilter = email
    ? aboutSection?.filter(item => item.label !== 'Recruiters Login')
    : aboutSection;

  const isSmall = isMedium && !isXXS;

  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

  const handleUbaTracking = (action = 'view') => {
    const trackingData = {
      pageName: 'Hamburger Menu',
      linkageName: 'Hamburger Menu Download App Button',
      linkageType: 'downloadButton',
      sessionUsername: sessionStorage.getItem('userId') || '',
      ...(action === 'click' && {typeClick: 'CTA'}),
    };
    TrackEventUBA(
      action === 'click' ? 'appDownloadLinkageClick' : 'appDownloadLinkageView',
      action,
      trackingData
    );
  };

  useEffect(() => {
    if (isAppIimjobs) {
      return;
    }
    openDrawer && handleUbaTracking('view');
  }, [openDrawer]);

  const trackTopMenuClick = (clickedMenuName: string) => {
    TrackEventUBA('topMenuClick', 'click', {
      section: 'Hamburger',
      page,
      sessionUsername: sessionStorage.getItem('userId') || '',
      clickedMenuName,
    });
  };

  const getTabListinfo = () => {
    if (isAppIimjobs) return tabsList;
    return getFilteredCategoryList(tabsList);
  };

  const getOrderedTabsList = () => {
    return getTabListinfo() ?? [];
  };

  const LinkItem = ({
    option,
    index,
  }: {
    option: ProfileDrawerOption;
    index: number;
  }) => (
    <ListItem
      component="a"
      key={`profiledrawer-listitem-${index}`}
      title={option['label']}
      href={getOldLink(option['url'])}
      target="_self"
      onClick={e => {
        e.preventDefault();
        const url = getOldLink(option['url']);
        deferTask(() => {
          trackTopMenuClick(option['label']);
          openUrl(url, '_self');
        });
      }}
      sx={{
        fontSize: theme.typography.pxToRem(14),
        fontWeight: theme.typography.fontWeightRegular,
        whiteSpace: 'wrap',
        cursor: 'pointer',
        color: theme.palette.secondary.main,
        wordBreak: 'break-word',
        ':hover': {
          color: theme.palette.secondary.main,
          textDecoration:
            option['label'] === 'Follow Up' && proMembership?.isValid
              ? 'none'
              : 'underline',
        },
        paddingLeft: 0,
      }}
    >
      {option['label']}
    </ListItem>
  );

  const ProfileFrame = () => (
    <>
      <Box>
        <Avatar
          imgUrl={profilePicUrl}
          showFallbackIcon={true}
          onClick={() => {
            deferTask(() => {
              trackTopMenuClick('EditProfile');
              openUrl(
                getOldLink(
                  '/registration/addPersonalDetails?profiletype=1&ref=profiledrw'
                ),
                '_self'
              );
            });
          }}
          sx={{
            width: isAppIimjobs ? 60 : 38,
            height: isAppIimjobs ? 60 : 38,
          }}
        />
      </Box>
      <Box
        display="block"
        sx={{
          float: 'left',
          marginLeft: theme.spacing(isAppIimjobs ? 1.375 : 2.375),
          width: theme.spacing(isAppIimjobs ? 22.5 : 25),
          '@media (width:320px)': {
            maxWidth: theme.spacing(20),
          },
          '@media (width:480px)': {
            width: theme.spacing(32),
          },
          '@media (width:1024px)': {
            width: theme.spacing(32),
          },
        }}
      >
        <Typography
          fontWeight={600}
          component="span"
          fontSize={theme.typography.pxToRem(isAppIimjobs ? 14 : 16)}
          overflow="hidden"
          whiteSpace="nowrap"
          display="flex"
          alignItems={'center'}
          color={theme.palette.secondary.main}
        >
          {name ? truncateString(name, isSmall ? 25 : 14) : 'Hello User'}

          <Box display="block" ml={theme.spacing(0.5)}>
            {proMembership?.isValid && (
              <Pro bgColor="linear-gradient(180deg, #FFB743 0%, #F99914 100%)" />
            )}
          </Box>
        </Typography>

        <Typography
          fontWeight={theme.typography.fontWeightMedium}
          component="span"
          fontSize={theme.typography.pxToRem(isAppIimjobs ? 12 : 14)}
          display="block"
          color={theme.palette.secondary.main}
        >
          {email
            ? truncateString(email, isSmall ? 25 : 20)
            : 'Please login to your account to apply to jobs.'}
        </Typography>

        {isAppIimjobs && (
          <Typography
            fontWeight={theme.typography.fontWeightMedium}
            component="a"
            fontSize={theme.typography.pxToRem(12)}
            display="block"
            color={theme.palette.primary.main}
            marginTop={theme.spacing(0.5)}
            sx={{
              cursor: 'pointer',
              textDecoration: 'none',
              color: theme.palette.primary.main,
            }}
            href={
              !phoneVerified
                ? getOldLink('/settings?section=verify-contact-details')
                : '/'
            }
            onClick={e => {
              e.preventDefault();
              const url = !phoneVerified
                ? getOldLink('/settings?section=verify-contact-details')
                : '/';
              deferTask(() => {
                trackTopMenuClick(
                  !phoneVerified && email
                    ? 'Verify Phone Number'
                    : !email
                      ? 'Login'
                      : ''
                );
                openUrl(url, '_self');
              });
            }}
          >
            {!phoneVerified && email
              ? 'Verify Phone Number'
              : !email
                ? 'Login to your account'
                : ''}
          </Typography>
        )}
        {!isAppIimjobs && (
          <Button
            variant="text"
            onClick={() => {
              deferTask(() => {
                TrackEventUBA('loginPageView', 'view', {
                  ref: getKeyFromPath('ref') || '',
                  pageName: 'Home Page',
                  sessionUsername: sessionStorage.getItem('userId') || '',
                });
                trackTopMenuClick('Login');
                handleDrawer(false);
                loginHandler && loginHandler();
              });
            }}
            sx={{
              width: '100%',
              fontSize: '14px',
              justifyContent: 'start',
              lineHeight: 'normal',
              p: 0,
              height: '40px',
              fontWeight: 600,
            }}
          >
            Login to your account
          </Button>
        )}
      </Box>
      {email && (
        <Box py={theme.spacing(1.5)}>
          <Image
            src={Edit}
            alt={'edit_profile'}
            width={32}
            height={32}
            objectFit="cover"
            style={{
              cursor: 'pointer',
            }}
            onClick={event => {
              event?.preventDefault();
              deferTask(() => {
                trackTopMenuClick('EditProfile');
                openUrl(
                  getOldLink(
                    '/registration/addPersonalDetails?profiletype=1&ref=profilemenu'
                  ),
                  '_self'
                );
              });
            }}
          />
        </Box>
      )}
    </>
  );

  return (
    <Drawer
      anchor="left"
      open={openDrawer}
      keepMounted
      onClose={() => {
        deferTask(() => {
          handleDrawer(false);
        });
      }}
      PaperProps={{
        sx: {
          padding: '0px 0px 15px',
          boxSizing: 'border-box',
          boxShadow: 2,
          borderRadius: isAppIimjobs ? '0px 16px 16px 0px' : '0',
          width: isAppIimjobs ? 'min-content' : 310,
        },
      }}
    >
      <Box width="100%" pb={10}>
        <Box
          display="flex"
          paddingTop={theme.spacing(1.5)}
          borderRadius={theme.spacing(1)}
          sx={
            isAppIimjobs
              ? {
                  mx: theme.spacing(2),
                  mt: theme.spacing(3),
                  mb: theme.spacing(1),
                  '@media (max-width:480px)': {
                    margin: theme.spacing(2),
                    mb: theme.spacing(1),
                    paddingTop: theme.spacing(0),
                  },
                }
              : {
                  '@media (max-width:480px)': {
                    padding: isAppIimjobs
                      ? '22px 20px 18px 19px!important'
                      : '22px 20px 10px 19px!important',
                  },
                  boxShadow: '0 6px 20px 0 rgba(0,0,0,.04)',
                }
          }
        >
          {!isAppIimjobs &&
            (email ? (
              <ProfileCard
                name={name}
                email={email}
                profileProgress={profileProgress}
                profileImgUrl={profilePicUrl}
                enableFull={true}
                setExpandProfile={setExpandProfile}
                showSpotLight={showSpotLight}
                appliedJobs={appliedJobCount}
                interviews={interviewSlots}
                savedJobs={savedJobCount}
                subscription={{
                  earlyApplicant: !!subscriptions?.earlyApplicant?.isValid,
                  resumeBuilder: !!subscriptions?.resumeBuilder?.isValid,
                }}
              />
            ) : (
              <ProfileFrame />
            ))}

          {isAppIimjobs && <ProfileFrame />}
        </Box>

        {isAppIimjobs && email && (
          <Box mt={0} mx={2}>
            <ProgressBar
              value={profileProgress ? profileProgress : 0}
              barColor="#EDBB4A"
            />
          </Box>
        )}

        {isAppIimjobs && email && !proMembership?.isValid && (
          <Box
            mt={theme.spacing(1)}
            mx={theme.spacing(2)}
            px={theme.spacing(1)}
            py={theme.spacing(1.5)}
            sx={{
              background:
                'linear-gradient(243deg, rgba(215, 220, 210, 0.96) 0%, rgba(215, 220, 210, 0.96) 61%, rgba(233, 222, 197, 0.96) 87%, rgba(215, 195, 226, 0.96) 100%, rgba(255, 255, 255, 0) 100%)',
              overflow: 'hidden',
              borderRadius: theme.spacing(1),
              justifyContent: 'space-between',
              alignItems: 'center',
              display: 'flex',
            }}
          >
            <Typography
              fontWeight={theme.typography.fontWeightMedium}
              fontSize={theme.typography.pxToRem(12)}
              mr={theme.spacing(2)}
              sx={{
                '@media (width: 320px)': {
                  fontSize: theme.typography.pxToRem(10),
                },
              }}
            >
              Enhance your
              <br />
              Job Search with PRO
            </Typography>
            <ButtonV2
              sx={{
                background: theme.palette.pro.main,
                py: 0.5,
                px: 1.5,
                fontSize: theme.typography.pxToRem(12),
                '@media (width: 320px)': {
                  fontSize: theme.typography.pxToRem(10),
                },
              }}
              variant="contained"
              size="small"
              href={getProLink(
                page || 'jf',
                reCap?.toString(),
                proMeta?.refCode
              )}
              target="_self"
              onClick={e => {
                e.preventDefault();
                const url = getProLink(
                  page || 'jf',
                  reCap?.toString(),
                  proMeta?.refCode
                );
                deferTask(() => {
                  openUrl(url, '_self');
                });
              }}
            >
              Upgrade to PRO
            </ButtonV2>
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            backgroundColor: '#fff',
            borderTop: isAppIimjobs ? '' : '1px solid #EFEFEF',
            boxShadow: isAppIimjobs ? '' : '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
          }}
        >
          <Button
            variant={!isAppIimjobs && email ? 'text' : 'outlined'}
            label="Download_App"
            size="large"
            color="primary"
            target="_blank"
            sx={{
              mx: 2,
              px: !isAppIimjobs && email ? 0 : 6,
              py: 1.25,
              mt: 2,
              mb: !isAppIimjobs && email ? 1.5 : 2,
              borderRadius: 6,
              lineHeight: '20px',
              fontSize: '16px',
              fontWeight: '500',
              display: 'flex',
              justifyContent: !isAppIimjobs && !email ? 'center' : 'start',
              background: !isAppIimjobs && !email ? '#FFFAF0' : 'transparent',
              width: isAppIimjobs ? 'auto' : '100%',
            }}
            onClick={() => {
              deferTask(() => {
                sendEvent && sendEvent('DownloadAppBtnMwebHamburger', 'Click');
                !isAppIimjobs && handleUbaTracking('click');
                trackTopMenuClick('DownloadApp');
                openUrl(
                  isAppIimjobs
                    ? getOldLink('https://iimjobs.onelink.me/TyLF/0q8qugj0')
                    : getAppDownloadLinkagesUrls(2),
                  '_blank'
                );
              });
            }}
          >
            <Image
              src={
                isAppIimjobs
                  ? Download
                  : `${process.env.NEXT_PUBLIC_STATIC_IIMJOBS}/static/images/download-hirist.svg`
              }
              alt={'Download'}
              width={18}
              height={18}
            />
            <Box px={1}>Download App</Box>
          </Button>
        </Box>
        <Box
          display="block"
          mt={theme.spacing(isAppIimjobs ? 1 : !expandProfile ? 1.875 : 1)}
        >
          {!expandProfile ? (
            (isAppIimjobs ? getOrderedTabsList() : getTabListinfo())?.map(
              (tabs, index) => {
                const {label, menu, tag = ''} = tabs || {};
                const toggleAcordion = (category: string) => () => {
                  setExpandedCategory(prev =>
                    prev === category ? null : category
                  );
                };

                return (
                  <Box key={`lotus-menudrawer-wrapper-${index}`}>

                    <Accordion
                      disableGutters
                      elevation={0}
                      square
                      expanded={expandedCategory === label}
                      key={`lotus-menudrawer-${index}`}
                      sx={{
                        '&:before': {
                          display: 'none',
                        },
                      }}
                    >
                      <AccordionSummary
                        onClick={() => {
                          deferTask(() => {
                            toggleAcordion(label)();
                          });
                        }}
                        expandIcon={
                          // <FaChevronDown cursor="pointer" onClick={toggleAcordion} />
                          <Image
                            src={ChevronDownLight}
                            alt={'open_nav_category'}
                            width={16}
                            height={16}
                            objectFit="cover"
                            style={{cursor: 'pointer'}}
                          />
                        }
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                        sx={{
                          marginTop: 0,
                          ...(isAppIimjobs && {flexDirection: 'row'}),
                        }}
                      >
                        {isAppIimjobs ? (
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              gap: '6px',
                              width: '100%',
                            }}
                          >
                            <Typography
                              fontWeight={
                                isAppIimjobs
                                  ? theme.typography.fontWeightMedium
                                  : 600
                              }
                              component="a"
                              href={`/c/${tag
                                .replace('&', '')
                                .replace(/\s+/g, '-')
                                .toLowerCase()}-jobs`}
                              fontSize={theme.typography.pxToRem(16)}
                              color={
                                isAppIimjobs
                                  ? theme.palette.secondary.main
                                  : '#555'
                              }
                              sx={{
                                textDecoration: 'none',
                                display: 'inline-flex',
                                alignItems: 'center',
                              }}
                              onClick={e => {
                                e.preventDefault();
                                const url = `/c/${tag
                                  .replace('&', '')
                                  .replace(/\s+/g, '-')
                                  .toLowerCase()}-jobs`;
                                deferTask(() => {
                                  trackTopMenuClick(label);
                                  openUrl(url, '_self');
                                });
                              }}
                            >
                              {label}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography
                            fontWeight={
                              isAppIimjobs
                                ? theme.typography.fontWeightMedium
                                : 600
                            }
                            component="a"
                            href={`/c/${tag
                              .replace('&', '')
                              .replace(/\s+/g, '-')
                              .toLowerCase()}-jobs`}
                            fontSize={theme.typography.pxToRem(16)}
                            display="block"
                            color={
                              isAppIimjobs
                                ? theme.palette.secondary.main
                                : '#555'
                            }
                            sx={{
                              textDecoration: 'none',
                            }}
                            onClick={e => {
                              e.preventDefault();
                              const url = `/c/${tag
                                .replace('&', '')
                                .replace(/\s+/g, '-')
                                .toLowerCase()}-jobs`;
                              deferTask(() => {
                                trackTopMenuClick(label);
                                openUrl(url, '_self');
                              });
                            }}
                          >
                            {label}
                          </Typography>
                        )}
                      </AccordionSummary>

                      {menu?.map((item, index) => {
                        const {tag = ''} = item;
                        return (
                          <AccordionDetails
                            key={`lotus-menudrawer-accordian-${index}`}
                            sx={{
                              backgroundColor: '#fff',
                              padding: `${theme.spacing(1)} ${theme.spacing(
                                2
                              )} ${theme.spacing(1)} ${theme.spacing(3.5)}`,
                            }}
                          >
                            <Link
                              href={`/k/${tag
                                .replace('&', '')
                                .replace(/\s+/g, '-')
                                .toLowerCase()}-jobs`}
                              target="_self"
                              sx={{
                                fontSize: theme.typography.pxToRem(16),
                                fontWeight: theme.typography.fontWeightRegular,
                                textDecoration: 'none',
                                color: theme.palette.secondary.light,
                                ':hover': {
                                  textDecoration: 'underline',
                                  color: theme.palette.secondary.dark,
                                },
                              }}
                              onClick={e => {
                                e.preventDefault();
                                const url = `/k/${tag
                                  .replace('&', '')
                                  .replace(/\s+/g, '-')
                                  .toLowerCase()}-jobs`;
                                deferTask(() => {
                                  trackTopMenuClick(item.label);
                                  openUrl(url, '_self');
                                });
                              }}
                            >
                              {item.label}
                            </Link>
                          </AccordionDetails>
                        );
                      })}
                    </Accordion>
                  </Box>
                );
              }
            )
          ) : (
            <>
              <ProfileDrop
                theme={theme}
                email={email}
                profileDrawerOptions={profileDrawerOptions ?? []}
                name={''}
                profileProgress={0}
                profilePicUrl={''}
                expandProfile={expandProfile}
                subscriptions={subscriptions}
                showPremium={
                  !(
                    subscriptions?.earlyApplicant?.isValid ||
                    subscriptions?.resumeBuilder?.isValid
                  )
                }
                OnLogoutBtnClick={function (): void {
                  throw new Error('Function not implemented.');
                }}
                page={page}
              />
            </>
          )}
        </Box>
        <>
          {!isAppIimjobs && (
            <hr
              style={{
                height: '1px',
                color: 'white',
                width: '90%',
                opacity: '0.55',
                margin: '15px 20px 5px !important',
              }}
            />
          )}
          {!isAppIimjobs &&
            [
              {
                label: 'Courses',
                link: '/learning?ref=nav',
                color: '#999999',
                fontWeight: 600,
              },
            ].map((item, index) => (
              <Box key={`label-non-${index}`} display={'block'} px={2} py={1.5}>
                <Link
                  my={1.5}
                  href={item.link}
                  target="_self"
                  sx={{
                    textDecoration: 'none',
                    color: item.color,
                    fontWeight: item.fontWeight,
                  }}
                  onClick={e => {
                    e.preventDefault();
                    const url = item.link;
                    deferTask(() => {
                      trackTopMenuClick(item.label);
                      openUrl(url, '_self');
                    });
                  }}
                >
                  {item.label}
                </Link>
                {item.fontWeight === 700 && (
                  <Typography
                    component={'span'}
                    sx={{
                      fontSize: 11,
                      ml: '5px',
                      p: '0 2px',
                      textAlign: 'center',
                      color: '#fff',
                      backgroundColor: '#ffad02',
                      borderRadius: '2px',
                      fontWeight: 700,
                    }}
                  >
                    New
                  </Typography>
                )}
              </Box>
            ))}
        </>

        {isAppIimjobs &&
          email &&
          profileDrawerOptions &&
          profileDrawerOptions?.length > 0 && (
            <Box margin={`${theme.spacing(1)} ${theme.spacing(2)}`}>
              <Box
                display="block"
                borderRadius={theme.spacing(2)}
                border={`1px rgba(126, 126, 126, 0.20) solid`}
              >
                <Typography
                  fontSize={theme.typography.pxToRem(16)}
                  fontWeight={theme.typography.fontWeightMedium}
                  mx={theme.spacing(2)}
                  marginTop={theme.spacing(2)}
                  marginBottom={theme.spacing(0)}
                >
                  {'Account Details'}
                </Typography>

                <List
                  sx={{
                    paddingLeft: theme.spacing(2),
                  }}
                >
                  {profileDrawerOptions?.map((option, index) => {
                    const isProFollowUp =
                      option['label'] === 'Follow Up' &&
                      typeof followUps === 'number' &&
                      followUps > 0;

                    if (isProFollowUp && proMembership?.isValid) {
                      return (
                        <Typography
                          component={'h2'}
                          sx={{
                            paddingY: 1,
                            paddingRight: 2,
                            fontSize: theme.typography.pxToRem(14),
                            fontWeight: theme.typography.fontWeightRegular,
                            whiteSpace: 'wrap',
                            cursor: 'pointer',
                            color: theme.palette.secondary.main,
                            wordBreak: 'break-word',
                            ':hover': {
                              color: theme.palette.secondary.main,
                              textDecoration:
                                option['label'] === 'Follow Up' &&
                                proMembership?.isValid
                                  ? 'none'
                                  : 'underline',
                            },
                            paddingLeft: 0,
                          }}
                        >
                          {`${option['label']} (${followUps})`}
                        </Typography>
                      );
                    }
                    const savedCount =
                      option['label'] === 'Saved Jobs' &&
                      typeof savedJobCount === 'number' &&
                      savedJobCount > 0
                        ? `${option['label']} (${savedJobCount})`
                        : option['label'];
                    return (
                      <LinkItem
                        option={{...option, label: savedCount}}
                        index={index}
                        key={index}
                      />
                    );
                  })}
                </List>
              </Box>
            </Box>
          )}

        {isAppIimjobs && (
          <Box
            display="flex"
            borderRadius={theme.spacing(2)}
            margin={`${theme.spacing(1)} ${theme.spacing(2)}`}
            border={`1px rgba(126, 126, 126, 0.20) solid`}
          >
            <Link
              href={process.env.NEXT_PUBLIC_LEARNING_CENTER}
              target="_blank"
              marginLeft={theme.spacing(2)}
              color="secondary.main"
              fontWeight={theme.typography.fontWeightMedium}
              lineHeight={2.5}
              sx={{
                textDecoration: 'none',
              }}
              onClick={e => {
                e.preventDefault();
                const url = process.env.NEXT_PUBLIC_LEARNING_CENTER;
                if (url) {
                  deferTask(() => {
                    trackTopMenuClick('My Learning Center');
                    openUrl(url, '_blank');
                  });
                }
              }}
            >
              {'My Learning Center'}
            </Link>
          </Box>
        )}
        {email && isAppIimjobs && (
          <>
            <Box
              display="flex"
              borderRadius={theme.spacing(2)}
              margin={`${theme.spacing(1)} ${theme.spacing(2)}`}
              border={`1px rgba(126, 126, 126, 0.20) solid`}
            >
              <Link
                href={getOldLink('/settings')}
                target="_self"
                marginLeft={theme.spacing(2)}
                color="secondary.main"
                fontWeight={theme.typography.fontWeightMedium}
                lineHeight={2.5}
                sx={{
                  textDecoration: 'none',
                }}
                onClick={e => {
                  e.preventDefault();
                  const url = getOldLink('/settings');
                  deferTask(() => {
                    trackTopMenuClick('My Settings');
                    openUrl(url, '_self');
                  });
                }}
              >
                {'My Settings'}
              </Link>
            </Box>
            <Box
              display="flex"
              borderRadius={theme.spacing(2)}
              margin={`${theme.spacing(1)} ${theme.spacing(2)}`}
              border={`1px rgba(126, 126, 126, 0.20) solid`}
            >
              <Link
                // href={getOldLink('/registration/changepassword.php')}
                href={'/settings?section=account-settings'}
                target="_self"
                marginLeft={theme.spacing(2)}
                color="secondary.main"
                fontWeight={theme.typography.fontWeightMedium}
                lineHeight={2.5}
                sx={{
                  textDecoration: 'none',
                }}
                onClick={e => {
                  e.preventDefault();
                  const url = '/settings?section=account-settings';
                  deferTask(() => {
                    trackTopMenuClick('Change Password');
                    openUrl(url, '_self');
                  });
                }}
              >
                {'Change Password'}
              </Link>
            </Box>
          </>
        )}
        {aboutSectionFilter?.length > 0 && isAppIimjobs && (
          <Box margin={theme.spacing(2)} mt={1}>
            <Box
              display="block"
              borderRadius={theme.spacing(2)}
              border={`1px rgba(126, 126, 126, 0.20) solid`}
            >
              <Typography
                fontSize={theme.typography.pxToRem(16)}
                fontWeight={theme.typography.fontWeightMedium}
                mx={theme.spacing(2)}
                marginTop={theme.spacing(2)}
                marginBottom={theme.spacing(1)}
              >
                {'About'}
              </Typography>

              <List
                sx={{
                  paddingLeft: theme.spacing(3.5),
                }}
              >
                {aboutSectionFilter.map(
                  (item, index) =>
                    item?.label && (
                      <ListItem
                        component="a"
                        key={`lotus-menudrawer-aboutsection-${index}`}
                        title={item['label']}
                        href={`${item?.link}`}
                        target="_blank"
                        onClick={e => {
                          e.preventDefault();
                          const url = `${item?.link}`;
                          deferTask(() => {
                            trackTopMenuClick(item?.label);
                            openUrl(url, '_blank');
                          });
                        }}
                        sx={{
                          display: 'list-item',
                          listStyleType: 'disc',
                          fontSize: theme.typography.pxToRem(14),
                          fontWeight: theme.typography.fontWeightRegular,
                          whiteSpace: 'wrap',
                          cursor: 'pointer',
                          color: theme.palette.secondary.main,
                          wordBreak: 'break-word',
                          ':hover': {
                            color: theme.palette.secondary.main,
                            textDecoration: 'underline',
                          },
                          paddingLeft: 0,
                          paddingTop: 0,
                        }}
                      >
                        {item?.label}
                      </ListItem>
                    )
                )}
              </List>
            </Box>
          </Box>
        )}
        {isAppIimjobs && email && OnLogoutBtnClick && (
          <Box
            display="flex"
            marginLeft={theme.spacing(2)}
            marginTop={theme.spacing(2)}
            position="sticky"
            sx={{backgroundColor: 'white'}}
          >
            <Link
              display="flex"
              sx={{
                textDecoration: 'none',
                cursor: 'pointer',
              }}
              onClick={() => {
                deferTask(() => {
                  trackTopMenuClick('Logout');
                  OnLogoutBtnClick();
                });
              }}
            >
              <Image
                src={Logout}
                alt={'logout'}
                width={20}
                height={20}
                objectFit="cover"
                style={{
                  cursor: 'pointer',
                }}
              />
              <Typography
                marginLeft={theme.spacing(1.5)}
                color="#d11124"
                fontSize={theme.typography.pxToRem(14)}
                fontWeight={theme.typography.fontWeightMedium}
              >
                {'Logout'}
              </Typography>
            </Link>
          </Box>
        )}
      </Box>
      {!isAppIimjobs && email && OnLogoutBtnClick && (
        <Box
          display="flex"
          bottom={0}
          position="fixed"
          sx={{backgroundColor: '#f2f5fa'}}
          height={50}
          width={'inherit'}
        >
          <Link
            display="flex"
            sx={{
              textDecoration: 'none',
              cursor: 'pointer',
              alignItems: 'center',
              ml: 2,
            }}
            onClick={() => {
              deferTask(() => {
                trackTopMenuClick('Logout');
                OnLogoutBtnClick();
              });
            }}
          >
            <Image
              src={SwitchOff}
              alt={'logout'}
              width={17}
              height={19}
              objectFit="cover"
              style={{
                cursor: 'pointer',
              }}
            />
            <Typography
              marginLeft={theme.spacing(1.5)}
              color="#555555"
              fontSize={theme.typography.pxToRem(16)}
              fontWeight={theme.typography.fontWeightMedium}
            >
              {'Log Out'}
            </Typography>
          </Link>
        </Box>
      )}
    </Drawer>
  );
};

export default MenuDrawer;
